"use client";

import { useEffect, useState } from "react";
import { MessageSquarePlus } from "lucide-react";
import { usePostHogState } from "modules/common/components/Providers/PostHogProvider";
import { getLoadedPostHog } from "utils/posthog-client";

const FEEDBACK_SURVEY_ID = "019fd6e0-30ca-0000-9609-ee2994df2bdd";

interface FeedbackSurveyTriggerProps {
  variant: "desktop" | "mobile";
}

export function FeedbackSurveyTrigger({ variant }: FeedbackSurveyTriggerProps) {
  const { hasAnalyticsConsent, isInitialized } = usePostHogState();
  const [isSurveyAvailable, setIsSurveyAvailable] = useState(false);

  useEffect(() => {
    if (!hasAnalyticsConsent || !isInitialized) {
      setIsSurveyAvailable(false);
      return;
    }

    const posthog = getLoadedPostHog();
    if (!posthog?.__loaded) {
      setIsSurveyAvailable(false);
      return;
    }

    return posthog.onSurveysLoaded((surveys, context) => {
      setIsSurveyAvailable(
        context?.isLoaded !== false &&
          surveys.some((survey) => survey.id === FEEDBACK_SURVEY_ID)
      );
    });
  }, [hasAnalyticsConsent, isInitialized]);

  if (!isSurveyAvailable) {
    return null;
  }

  const isDesktop = variant === "desktop";

  const handleClick = () => {
    const posthog = getLoadedPostHog();
    if (!posthog?.__loaded) return;

    posthog.displaySurvey(FEEDBACK_SURVEY_ID, {
      displayType: "popover",
      ignoreConditions: true,
      ignoreDelay: true,
    });

    if (isDesktop) return;

    const drawerToggle = document.getElementById(
      "mobile-menu-drawer"
    ) as HTMLInputElement | null;

    if (drawerToggle?.checked) {
      drawerToggle.checked = false;
      drawerToggle.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };

  if (isDesktop) {
    return (
      <div className="pointer-events-none fixed inset-y-0 right-0 z-50 hidden items-center md:flex">
        <button
          type="button"
          aria-label="Send feedback or report an issue"
          className="btn btn-sm border-base-300 bg-base-100 text-base-content hover:bg-base-200 pointer-events-auto h-auto min-h-0 flex-col gap-2 rounded-l-md rounded-r-none border-r-0 px-2 py-3 shadow-lg"
          onClick={handleClick}
        >
          <span className="font-semibold tracking-wide [text-orientation:mixed] [writing-mode:vertical-rl]">
            Feedback
          </span>
          <MessageSquarePlus size={18} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-label="Send feedback or report an issue"
      className="btn btn-ghost w-full justify-start gap-2 px-3 md:hidden"
      onClick={handleClick}
    >
      <MessageSquarePlus size={16} aria-hidden="true" />
      Send feedback
    </button>
  );
}
